import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Injectable } from '@angular/core';
import { PickupService } from '../../data/pickup.service';
import {
    pickupCancelFailure,
    pickupCancelRequest, pickupCancelSuccess,
    pickupSelectShipments,
    pickupSetAvailability,
    pickupSubmitFailure,
    pickupSubmitRequest,
    pickupSubmitSuccess
} from './pickup.actions';
import { AvailabilityService } from '../../modules/shared/services/availability.service';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { PickupBookingService } from '../../modules/shared/services/pickup-booking.service';
import { selectPickupBookingData, selectPickupSelection, selectPickupShipmentSelection, selectPickupState } from './pickup.state';
import { Store } from '@ngrx/store';
import { AppState } from '../index';
import { ActionStatusEnum } from '../../enum/action-status.enum';
import { of } from 'rxjs';
import { LocaleService } from '../../modules/shared/services/locale.service';
import { PickupResult } from '../../models/pickup-result';
import { ServerExceptionEnum } from '../../enum/server-exception.enum';
import { shipmentTableRequest } from '../shipments-table/shipments-table.actions';
import { pickupTableRequest } from '../pickup-table/pickup-table.actions';

@Injectable()
export class PickupEffects {

    constructor(private actions$: Actions, private pickupService: PickupService, private availabilityService: AvailabilityService,
                private pickupBookingService: PickupBookingService, private store: Store<AppState>, private localeService: LocaleService) {
    }

    pickupSelectShipments$ = createEffect(() => this.actions$.pipe(
        ofType(pickupSelectShipments),
        mergeMap(action => this.availabilityService.queryByShipments(action.shipmentSelection)),
        map(availability => pickupSetAvailability({ availability }))
    ));

    pickupSubmit$ = createEffect(() => this.actions$.pipe(
        ofType(pickupSubmitRequest),
        withLatestFrom(this.store.select(selectPickupBookingData)),
        mergeMap(([ , data ]) => this.pickupBookingService.bookPickupForShipments(data.selection, data.date, data.readyTime,
            data.closeTime, data.parameters).pipe(
            filter(action => action.status === ActionStatusEnum.COMPLETED || action.status === ActionStatusEnum.ERROR),
            map(action => {
                if (action.status === ActionStatusEnum.COMPLETED) {
                    const id = Object.keys(action.output)[0];
                    const output = action.output[id] as PickupResult;
                    if (output.result === ServerExceptionEnum.SUCCESS) {
                        return pickupSubmitSuccess();
                    } else {
                        return pickupSubmitFailure({ messages: output.messages });
                    }
                } else {
                    return pickupSubmitFailure({ messages: action.errors });
                }
            }),
            catchError((err) => of(pickupSubmitFailure({ messages: [ this.localeService.createServerMessage(err.message) ] })))
        ))
    ));

    pickupCancel$ = createEffect(() => this.actions$.pipe(
        ofType(pickupCancelRequest),
        withLatestFrom(this.store.select(selectPickupSelection)),
        mergeMap(([ , selection ]) => this.pickupBookingService.cancelPickups(selection).pipe(
            filter(action => action.status === ActionStatusEnum.COMPLETED || action.status === ActionStatusEnum.ERROR),
            map(action => {
                if (action.status === ActionStatusEnum.COMPLETED) {
                    const id = Object.keys(action.output)[0];
                    const output = action.output[id] as PickupResult;
                    if (output.result === ServerExceptionEnum.SUCCESS) {
                        return pickupCancelSuccess();
                    } else {
                        return pickupCancelFailure({ messages: output.messages });
                    }
                } else {
                    return pickupCancelFailure({ messages: action.errors });
                }
            }),
            catchError((err) => of(pickupCancelFailure({ messages: [ this.localeService.createServerMessage(err.message) ] })))
        ))
    ));

    pickupCancelRefreshShipments$ = createEffect(() => this.actions$.pipe(
        ofType(pickupCancelSuccess),
        map(() => shipmentTableRequest())
    ));

    pickupCancelRefreshPickups$ = createEffect(() => this.actions$.pipe(
        ofType(pickupCancelSuccess),
        map(() => pickupTableRequest())
    ));

    pickupSubmitRefreshShipments$ = createEffect(() => this.actions$.pipe(
        ofType(pickupSubmitSuccess),
        map(() => shipmentTableRequest())
    ));

    pickupSubmitRefreshPickups$ = createEffect(() => this.actions$.pipe(
        ofType(pickupSubmitSuccess),
        map(() => pickupTableRequest())
    ));

}
